import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import api from "../../api";
import {
  Phone,
  Play,
  Pause,
  Settings,
  Volume2,
  Clock,
  Users,
  MessageSquare,
  BarChart3,
  Plus,
  Trash2,
  Edit3,
  Mic,
  VolumeX,
  CheckCircle2,
  X,
  Save,
} from "lucide-react";
import { hasAdminAccess } from "../../utils/roleAccess";

const AiCallCenter = () => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isCallActive, setIsCallActive] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("female_1");
  const [selectedLanguage, setSelectedLanguage] = useState("en-AU");

  // State for dynamic call statistics
  const [statistics, setStatistics] = useState({
    totalCalls: 0,
    activeNow: 0,
    successRate: 0,
    avgDuration: "0:00",
    callbacksScheduled: 0,
    leadsCaptured: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [activeCalls, setActiveCalls] = useState([]);

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };

  // State for scripts management
  const [callScripts, setCallScripts] = useState([
    {
      id: 1,
      name: "Standard Greeting",
      script: `Hello, this is {agent_name} from Collings Real Estate. I hope you're having a great day. I'm calling about your recent property inquiry.`,
      isDefault: true,
    },
    {
      id: 2,
      name: "Property Follow-up",
      script: `Hi, this is {agent_name} following up about the property at {property_address} that you recently viewed on our website.`,
      isDefault: false,
    },
    {
      id: 3,
      name: "Market Update",
      script: `Good {time_of_day}, this is {agent_name} from Collings. I noticed you're interested in the {suburb} area, and I have some exciting market updates to share.`,
      isDefault: false,
    },
  ]);

  // State for script modal
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [scriptForm, setScriptForm] = useState({
    name: "",
    script: "",
    isDefault: false,
  });

  // Voice options
  const voiceOptions = [
    { id: "female_1", name: "Sarah", gender: "Female", accent: "Australian" },
    { id: "female_2", name: "Emma", gender: "Female", accent: "British" },
    { id: "male_1", name: "James", gender: "Male", accent: "Australian" },
    { id: "male_2", name: "Michael", gender: "Male", accent: "American" },
  ];

  // Language options
  const languageOptions = [
    { code: "en-AU", name: "English (Australia)" },
    { code: "en-GB", name: "English (UK)" },
    { code: "en-US", name: "English (US)" },
    { code: "zh-CN", name: "Chinese (Mandarin)" },
    { code: "hi-IN", name: "Hindi" },
    { code: "vi-VN", name: "Vietnamese" },
  ];

  // AI Behavior settings
  const [aiSettings, setAiSettings] = useState({
    speechRate: 1,
    politenessLevel: "professional",
    interruptionHandling: "polite",
    maxCallDuration: 300, // 5 minutes
    callbackScheduling: true,
    sentimentAnalysis: true,
    automaticFollowup: true,
  });

  // Fetch call statistics from backend
  useEffect(() => {
    const fetchCallStatistics = async () => {
      try {
        setStatsLoading(true);
        setStatsError(null);

        const response = await api.get("api/ai-voice-caller/agent-calls/");
        const calls = response.data || [];

        // Calculate statistics from call data
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        // Filter calls from last 30 days
        const recentCalls = calls.filter(call => {
          const callDate = new Date(call.created_at);
          return callDate >= thirtyDaysAgo;
        });

        // Calculate active calls (in progress)
        const activeCallsList = calls.filter(call =>
          call.status?.toLowerCase() === 'in progress' ||
          call.status?.toLowerCase() === 'pending'
        );

        // Calculate success rate (completed calls with interested status)
        const completedCalls = recentCalls.filter(call =>
          call.status?.toLowerCase() === 'completed'
        );
        const successfulCalls = completedCalls.filter(call => call.is_interested);
        const successRate = completedCalls.length > 0
          ? Math.round((successfulCalls.length / completedCalls.length) * 100)
          : 0;

        // Calculate average duration (mock for now, would need duration field)
        const avgDuration = "4:32"; // TODO: Calculate from actual call durations

        // Count callbacks scheduled (mock for now)
        const callbacksScheduled = recentCalls.filter(call =>
          call.timeline?.toLowerCase() === 'callback requested'
        ).length;

        // Count leads captured (interested calls)
        const leadsCaptured = successfulCalls.length;

        setStatistics({
          totalCalls: recentCalls.length,
          activeNow: activeCallsList.length,
          successRate,
          avgDuration,
          callbacksScheduled,
          leadsCaptured,
        });

        // Set active calls for display
        setActiveCalls(activeCallsList.slice(0, 3)); // Show top 3 active calls

      } catch (err) {
        console.error("Failed to load call statistics:", err);
        setStatsError("Failed to load call statistics");
        toast.error("Failed to load call statistics");
      } finally {
        setStatsLoading(false);
      }
    };

    fetchCallStatistics();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchCallStatistics, 30000);
    return () => clearInterval(interval);
  }, []);

  const renderDashboard = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Loading State */}
      {statsLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading call statistics...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {statsError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p className="font-medium">Error loading statistics</p>
          <p className="text-sm">{statsError}</p>
        </div>
      )}

      {/* Statistics Cards */}
      {!statsLoading && !statsError && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-1.5 sm:gap-6">
          <StatCard
            icon={Phone}
            title="Total"
            value={statistics.totalCalls}
            subtitle="Past 30d"
            color="blue"
          />
          <StatCard
            icon={Users}
            title="Active"
            value={statistics.activeNow}
            subtitle="Now"
            color="green"
          />
          <StatCard
            icon={CheckCircle2}
            title="Success"
            value={`${statistics.successRate}%`}
            subtitle="Rate"
            color="purple"
          />
          <StatCard
            icon={Clock}
            title="Avg"
            value={statistics.avgDuration}
            subtitle="Min/call"
            color="orange"
          />
          <StatCard
            icon={MessageSquare}
            title="Callbacks"
            value={statistics.callbacksScheduled}
            subtitle="Count"
            color="pink"
          />
          <StatCard
            icon={Users}
            title="Leads"
            value={statistics.leadsCaptured}
            subtitle="Total"
            color="indigo"
          />
        </div>
      )}

      {/* Active Calls Section */}
      {!statsLoading && !statsError && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium mb-4">Active Calls</h3>
          <div className="space-y-4">
            {activeCalls.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No active calls at the moment</p>
            ) : (
              activeCalls.map((call) => (
                <div
                  key={call.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 rounded-xl border border-gray-100 hover:bg-gray-50 gap-3"
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: call.call_source === 'buyer_enquiry' ? '#dcfce7' : '#dbeafe'
                      }}
                    >
                      <Phone
                        className="w-5 h-5"
                        style={{
                          color: call.call_source === 'buyer_enquiry' ? '#16a34a' : '#2563eb'
                        }}
                      />
                    </div>
                    <div>
                      <p className="font-medium">{call.phone_number || 'Unknown'}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-500">
                          {call.status ? call.status.charAt(0).toUpperCase() + call.status.slice(1) : 'In Progress'}
                        </p>
                        {call.call_source && (
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{
                              backgroundColor: call.call_source === 'buyer_enquiry' ? '#dcfce7' : '#dbeafe',
                              color: call.call_source === 'buyer_enquiry' ? '#16a34a' : '#2563eb'
                            }}
                          >
                            {call.call_source === 'buyer_enquiry' ? 'Enquiry' : 'Property'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Intent</p>
                      <p className="font-medium">
                        {call.intent ? call.intent.charAt(0).toUpperCase() + call.intent.slice(1) : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderVoiceSettings = () => (
    <div className="space-y-6">
      {/* Voice Selection */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-base sm:text-lg font-medium mb-4">Voice Selection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {voiceOptions.map((voice) => (
            <div
              key={voice.id}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedVoice === voice.id
                ? "border-blue-500 bg-blue-50"
                : "border-gray-100 hover:bg-gray-50"
                }`}
              onClick={() => setSelectedVoice(voice.id)}
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Mic className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium">{voice.name}</p>
                  <p className="text-sm text-gray-500">
                    {voice.gender} • {voice.accent}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Language Selection */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-base sm:text-lg font-medium mb-4">Language & Region</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {languageOptions.map((lang) => (
            <div
              key={lang.code}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedLanguage === lang.code
                ? "border-blue-500 bg-blue-50"
                : "border-gray-100 hover:bg-gray-50"
                }`}
              onClick={() => setSelectedLanguage(lang.code)}
            >
              <p className="font-medium">{lang.name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Voice Behavior */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-base sm:text-lg font-medium mb-4">Voice Behavior</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Speech Rate
            </label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={aiSettings.speechRate}
              onChange={(e) =>
                setAiSettings({
                  ...aiSettings,
                  speechRate: parseFloat(e.target.value),
                })
              }
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500">
              <span>Slower</span>
              <span>Normal</span>
              <span>Faster</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Politeness Level
            </label>
            <select
              value={aiSettings.politenessLevel}
              onChange={(e) =>
                setAiSettings({
                  ...aiSettings,
                  politenessLevel: e.target.value,
                })
              }
              className="w-full p-2 rounded-lg border border-gray-200"
            >
              <option value="casual">Casual</option>
              <option value="professional">Professional</option>
              <option value="formal">Formal</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // Script management handlers
  const handleNewScript = () => {
    setEditingScript(null);
    setScriptForm({
      name: "",
      script: "",
      isDefault: false,
    });
    setShowScriptModal(true);
  };

  const handleEditScript = (script) => {
    setEditingScript(script);
    setScriptForm({
      name: script.name,
      script: script.script,
      isDefault: script.isDefault,
    });
    setShowScriptModal(true);
  };

  const handleDeleteScript = (scriptId) => {
    if (window.confirm("Are you sure you want to delete this script?")) {
      setCallScripts(callScripts.filter((s) => s.id !== scriptId));
      toast.success("Script deleted successfully");
    }
  };

  const handleSaveScript = () => {
    if (!scriptForm.name.trim() || !scriptForm.script.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (editingScript) {
      // Update existing script
      setCallScripts(
        callScripts.map((s) =>
          s.id === editingScript.id
            ? { ...s, ...scriptForm }
            : { ...s, isDefault: scriptForm.isDefault ? false : s.isDefault }
        )
      );
      toast.success("Script updated successfully");
    } else {
      // Create new script
      const newScript = {
        id: Math.max(...callScripts.map((s) => s.id), 0) + 1,
        ...scriptForm,
      };
      setCallScripts([
        ...callScripts.map((s) => ({ ...s, isDefault: false })),
        newScript,
      ]);
      toast.success("Script created successfully");
    }

    setShowScriptModal(false);
    setEditingScript(null);
    setScriptForm({
      name: "",
      script: "",
      isDefault: false,
    });
  };

  // Memoized input change handlers to prevent re-renders
  const handleScriptNameChange = useCallback((e) => {
    const value = e.target.value;
    setScriptForm(prev => ({ ...prev, name: value }));
  }, []);

  const handleScriptContentChange = useCallback((e) => {
    const value = e.target.value;
    setScriptForm(prev => ({ ...prev, script: value }));
  }, []);

  const handleScriptDefaultChange = useCallback((e) => {
    const checked = e.target.checked;
    setScriptForm(prev => ({ ...prev, isDefault: checked }));
  }, []);

  const renderScriptManager = () => (
    <div className="space-y-6">
      {/* Script List */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium">Call Scripts</h3>
          <button
            onClick={handleNewScript}
            className="px-4 py-2 rounded-xl text-white flex items-center space-x-2 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: brandColors.secondary }}
          >
            <Plus className="w-5 h-5" />
            <span>New Script</span>
          </button>
        </div>

        <div className="space-y-4">
          {callScripts.map((script) => (
            <div
              key={script.id}
              className="p-4 rounded-xl border border-gray-100 hover:bg-gray-50"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium">{script.name}</h4>
                  {script.isDefault && (
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                      Default
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditScript(script)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Edit Script"
                  >
                    <Edit3 className="w-5 h-5 text-gray-400" />
                  </button>
                  <button
                    onClick={() => handleDeleteScript(script.id)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                    title="Delete Script"
                    disabled={script.isDefault}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <p className="text-gray-600 text-sm">{script.script}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Variables Guide */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-lg font-medium mb-4">Available Variables</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { name: "{agent_name}", desc: `Current agent's name` },
            { name: "{property_address}", desc: "Property address" },
            { name: "{suburb}", desc: "Property suburb" },
            { name: "{time_of_day}", desc: "Morning/Afternoon/Evening" },
            { name: "{client_name}", desc: `Client's name` },
            { name: "{company_name}", desc: "Company name" },
          ].map((variable) => (
            <div key={variable.name} className="flex items-center space-x-2">
              <code className="px-2 py-1 rounded bg-gray-100 text-sm">
                {variable.name}
              </code>
              <span className="text-sm text-gray-500">{variable.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Script Modal */}
      {showScriptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold">
                {editingScript ? "Edit Script" : "New Script"}
              </h3>
              <button
                onClick={() => {
                  setShowScriptModal(false);
                  setEditingScript(null);
                  setScriptForm({
                    name: "",
                    script: "",
                    isDefault: false,
                  });
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Script Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={scriptForm.name}
                  onChange={handleScriptNameChange}
                  placeholder="Enter script name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Script Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={scriptForm.script}
                  onChange={handleScriptContentChange}
                  placeholder="Enter your script here. Use variables like {agent_name}, {property_address}, etc."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] resize-none"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={scriptForm.isDefault}
                  onChange={handleScriptDefaultChange}
                  className="w-4 h-4 text-[#55C5D0] border-gray-300 rounded focus:ring-[#55C5D0]"
                />
                <label
                  htmlFor="isDefault"
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  Set as default script
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowScriptModal(false);
                    setEditingScript(null);
                    setScriptForm({
                      name: "",
                      script: "",
                      isDefault: false,
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveScript}
                  className="px-4 py-2 rounded-lg text-white flex items-center space-x-2 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: brandColors.secondary }}
                >
                  <Save className="w-4 h-4" />
                  <span>{editingScript ? "Update" : "Create"} Script</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );


  return (
    <div className="space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-0.5">
        <div className="text-left">
          <h2 className="text-xl sm:text-3xl font-semibold text-[#55C5D0]">AI Call Center</h2>
          <p className="text-xs sm:text-base text-gray-400 mt-1">
            {isAdmin
              ? "Manage settings"
              : "Monitor calls"}
          </p>
        </div>
        <button
          onClick={() => setIsCallActive(!isCallActive)}
          className={`w-full sm:w-auto px-6 py-2.5 rounded-xl text-white flex items-center justify-center space-x-2 transition-all active:scale-95 shadow-md ${isCallActive ? "bg-red-500" : "bg-green-500"
            }`}
        >
          {isCallActive ? (
            <>
              <Pause className="w-4 h-4 sm:w-5 h-5 fill-current" />
              <span className="font-medium text-sm sm:text-base">Pause Calls</span>
            </>
          ) : (
            <>
              <Play className="w-4 h-4 sm:w-5 h-5 fill-current" />
              <span className="font-medium text-sm sm:text-base">Start Calls</span>
            </>
          )}
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 bg-gray-100/80 backdrop-blur-sm rounded-xl p-1 overflow-x-auto no-scrollbar sticky top-0 z-10 mx-0">
        {[
          { id: "dashboard", icon: BarChart3, label: "Dashboard" },
          { id: "voice", icon: Volume2, label: "Voice" },
          { id: "scripts", icon: MessageSquare, label: "Scripts" },
          { id: "settings", icon: Settings, label: "Settings" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-2 rounded-lg transition-all whitespace-nowrap ${activeTab === tab.id
              ? "bg-white text-gray-900 shadow-sm font-semibold"
              : "text-gray-500 hover:text-gray-900"
              }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            <span className="text-[11px] sm:text-sm">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === "dashboard" && renderDashboard()}
      {activeTab === "voice" && renderVoiceSettings()}
      {activeTab === "scripts" && renderScriptManager()}
      {activeTab === "settings" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="text-lg font-medium mb-6">AI Behavior Settings</h3>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Call Duration
                </label>
                <select
                  value={aiSettings.maxCallDuration}
                  onChange={(e) =>
                    setAiSettings({
                      ...aiSettings,
                      maxCallDuration: parseInt(e.target.value),
                    })
                  }
                  className="w-full p-2 rounded-lg border border-gray-200"
                >
                  <option value="180">3 minutes</option>
                  <option value="300">5 minutes</option>
                  <option value="600">10 minutes</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interruption Handling
                </label>
                <select
                  value={aiSettings.interruptionHandling}
                  onChange={(e) =>
                    setAiSettings({
                      ...aiSettings,
                      interruptionHandling: e.target.value,
                    })
                  }
                  className="w-full p-2 rounded-lg border border-gray-200"
                >
                  <option value="polite">Polite Pause</option>
                  <option value="direct">Direct Response</option>
                  <option value="ignore">Continue Speaking</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100">
                <div>
                  <p className="font-medium">Automatic Callback Scheduling</p>
                  <p className="text-sm text-gray-500">
                    Allow AI to schedule callbacks when requested
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiSettings.callbackScheduling}
                    onChange={(e) =>
                      setAiSettings({
                        ...aiSettings,
                        callbackScheduling: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100">
                <div>
                  <p className="font-medium">Sentiment Analysis</p>
                  <p className="text-sm text-gray-500">
                    Analyze caller sentiment to adjust responses
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiSettings.sentimentAnalysis}
                    onChange={(e) =>
                      setAiSettings({
                        ...aiSettings,
                        sentimentAnalysis: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100">
                <div>
                  <p className="font-medium">Automatic Follow-up</p>
                  <p className="text-sm text-gray-500">
                    Send automated follow-up messages after calls
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiSettings.automaticFollowup}
                    onChange={(e) =>
                      setAiSettings({
                        ...aiSettings,
                        automaticFollowup: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
  <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-2 sm:p-5 flex flex-col h-full shadow-sm">
    <div className="flex items-center justify-between mb-1.5 sm:mb-2">
      <div
        className={`w-7 h-7 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center bg-${color}-50`}
      >
        <Icon className={`w-3.5 h-3.5 sm:w-5 h-5 text-${color}-600`} />
      </div>
      <p className="text-[9px] sm:text-xs font-semibold text-gray-400 uppercase tracking-tight">{title}</p>
    </div>
    <div className="mt-auto">
      <h3 className="text-base sm:text-2xl font-bold text-gray-800 leading-none">{value}</h3>
      <p className="text-[9px] sm:text-xs text-gray-400 mt-1 line-clamp-1">{subtitle}</p>
    </div>
  </div>
);

export default AiCallCenter;
